import React from 'react';
import { FormattedPlural } from 'react-intl';

import { TradeListLoadingStates } from '../reducer/constants';

/**
 * Generates loading overlay configuration based on the current loading state.
 * This function maps the TradeList loading state machine's current state to
 * the appropriate overlay configuration, providing user feedback during the
 * multi-stage trade data fetching pipeline. Each loading stage displays
 * different progress messages and progress bar values.
 * @param {object} params - The parameters object.
 * @param {object} params.intl - The react-intl intl object for internationalization.
 *   Used to format all overlay text messages with proper locale support.
 * @param {object} params.date - A Luxon DateTime object representing the date
 * being loaded. Displayed in the overlay body to provide context to the user.
 * @param {object} params.loadingState - The current loading state from the reducer.
 * @returns {{ loadingOverlayProps: object|null, isOverlayVisible: boolean }} Overlay configuration
 * object with `loadingOverlayProps` and `isOverlayVisible` properties. See return value
 * structure documentation above.
 */
const buildLoadingOverlayState = ({
  intl,
  date,
  loadingState,
}) => {
  const { state, metadata } = loadingState;
  const {
    metersCount, rulesCount, tradesCount,
  } = metadata;

  const headerLabel = intl.formatMessage({
    id: 'trade.trade_list.helpers.buildLoadingOverlayState.overlay.header.label',
    defaultMessage: 'Loading Trade Data',
  });
  const bodyLabel = intl.formatMessage({
    id: 'trade.trade_list.helpers.buildLoadingOverlayState.overlay.body.label',
    defaultMessage: 'Loading trade data for {dateRange}.',
  }, { dateRange: date.toISODate() });

  const propertyLoadedProgressBarLabel = intl.formatMessage({
    id: 'trade.trade_list.helpers.buildLoadingOverlayState.overlay.property_loaded.body.label',
    defaultMessage: 'Stage 1 of 6 completed. Property loaded. Loading meters...',
  });

  const singleMeter = intl.formatMessage({ id: 'trade.trade_list.helpers.buildLoadingOverlayState.overlay.meter.singular', defaultMessage: 'meter' });
  const multipleMeters = intl.formatMessage({ id: 'trade.trade_list.helpers.buildLoadingOverlayState.overlay.meter.multiple', defaultMessage: 'meters' });

  const metersLoadedProgressBarLabel = intl.formatMessage(
    { id: 'trade.trade_list.helpers.buildLoadingOverlayState.overlay.meters_loaded.overlay.body.label', defaultMessage: 'Stage 2 of 6 completed. Loaded {loadedMeters} {meterCountLabel}. Loading trade rules...' },
    {
      loadedMeters: <strong>{metersCount}</strong>,
      meterCountLabel: <FormattedPlural
        value={metersCount}
        one={singleMeter}
        other={multipleMeters}
      />,
    },
  );

  const singleRule = intl.formatMessage({ id: 'trade.trade_list.helpers.buildLoadingOverlayState.overlay.rule.singular', defaultMessage: 'rule' });
  const multipleRules = intl.formatMessage({ id: 'trade.trade_list.helpers.buildLoadingOverlayState.overlay.rule.multiple', defaultMessage: 'rules' });

  const tradeRulesLoadedProgressBarLabel = intl.formatMessage(
    { id: 'trade.trade_list.helpers.buildLoadingOverlayState.overlay.trade_rules_loaded.overlay.body.label', defaultMessage: 'Stage 3 of 6 completed. Loaded {loadedTradeRules} {tradeRulesCountLabel}. Loading trades...' },
    {
      loadedTradeRules: <strong>{rulesCount}</strong>,
      tradeRulesCountLabel: <FormattedPlural
        value={rulesCount}
        one={singleRule}
        other={multipleRules}
      />,
    },
  );

  const singleTrade = intl.formatMessage({ id: 'trade.trade_list.helpers.buildLoadingOverlayState.overlay.trade.singular', defaultMessage: 'trade' });
  const multipleTrades = intl.formatMessage({ id: 'trade.trade_list.helpers.buildLoadingOverlayState.overlay.trade.multiple', defaultMessage: 'trades' });

  const tradesLoadedProgressBarLabel = intl.formatMessage(
    { id: 'trade.trade_list.helpers.buildLoadingOverlayState.overlay.trades_loaded.overlay.body.label', defaultMessage: 'Stage 4 of 6 completed. Loaded {loadedTrades} {tradesCountLabel}. Rendering...' },
    {
      loadedTrades: <strong>{tradesCount}</strong>,
      tradesCountLabel: <FormattedPlural
        value={tradesCount}
        one={singleTrade}
        other={multipleTrades}
      />,
    },
  );
  const tradeDataPreparedProgressBarLabel = intl.formatMessage({
    id: 'trade.trade_list.helpers.buildLoadingOverlayState.overlay.trade_data_prepared.overlay.body.label',
    defaultMessage: 'Stage 5 of 6 completed. Trade data prepared.',
  });

  const tradeDataRenderedProgressBarLabel = intl.formatMessage({
    id: 'trade.trade_list.helpers.buildLoadingOverlayState.overlay.trade_data_rendered.overlay.body.label',
    defaultMessage: 'Stage 6 of 6 completed. Trade data rendered.',
  });

  switch (state) {
    case TradeListLoadingStates.IDLE:
    case TradeListLoadingStates.ERROR:
      return { loadingOverlayProps: null, isOverlayVisible: false };
    case TradeListLoadingStates.INITIALISING:
      return {
        loadingOverlayProps: {
          headerLabel,
          bodyLabel,
          progressLabel: propertyLoadedProgressBarLabel,
          updateProgress: () => {},
          progressBarValue: 5,
        },
        isOverlayVisible: true,
      };
    case TradeListLoadingStates.LOADING_METERS:
      return {
        loadingOverlayProps: {
          headerLabel,
          bodyLabel,
          progressLabel: propertyLoadedProgressBarLabel,
          updateProgress: () => {},
          progressBarValue: 15,
        },
        isOverlayVisible: true,
      };
    case TradeListLoadingStates.LOADING_RULES:
      return {
        loadingOverlayProps: {
          headerLabel,
          bodyLabel,
          progressLabel: metersLoadedProgressBarLabel,
          updateProgress: () => {},
          progressBarValue: 30,
        },
        isOverlayVisible: true,
      };
    case TradeListLoadingStates.LOADING_TRADES:
      return {
        loadingOverlayProps: {
          headerLabel,
          bodyLabel,
          progressLabel: tradeRulesLoadedProgressBarLabel,
          updateProgress: () => {},
          progressBarValue: 60,
        },
        isOverlayVisible: true,
      };
    case TradeListLoadingStates.PREPARING_DATA:
      return {
        loadingOverlayProps: {
          headerLabel,
          bodyLabel,
          progressLabel: tradesLoadedProgressBarLabel,
          updateProgress: () => {},
          progressBarValue: 90,
        },
        isOverlayVisible: true,
      };
    case TradeListLoadingStates.RENDERING_DATA:
      return {
        loadingOverlayProps: {
          headerLabel,
          bodyLabel,
          progressLabel: tradeDataPreparedProgressBarLabel,
          updateProgress: () => {},
          progressBarValue: 95,
        },
        isOverlayVisible: true,
      };
    case TradeListLoadingStates.COMPLETE:
      return {
        loadingOverlayProps: {
          headerLabel,
          bodyLabel,
          progressLabel: tradeDataRenderedProgressBarLabel,
          updateProgress: () => {},
          progressBarValue: 100,
        },
        isOverlayVisible: true,
      };
    default:
      return { loadingOverlayProps: null, isOverlayVisible: false };
  }
};

export default buildLoadingOverlayState;
